import { DEFAULT_BAR_WEIGHT, DEFAULT_INCREMENTS, DEFAULT_PLATES, DEFAULT_START_WEIGHTS, FITNESS_PROGRAMS, MAIN_LIFTS, liftName, createFitnessWorkout, createFitnessBodyweight, normalizeFitnessSettings, } from '../models/fitness';
import { parseDateKey, shiftDateKey, weekKeys, mondayKey, habitScheduledOn, habitDueDates, habitPausedOn, scheduleSubject } from './habit-schedule';
import { daysBetween } from '../utils/date-utils';
import { createTask, applyTaskUpdate } from '../models/task';
import { createGoal } from '../models/goal';
import { v4 as uuidv4 } from 'uuid';
/**
 * Monday of the week containing `date`. `mondayKey`, not `weekKeys(d)[0]`:
 * the weekly streak asks once per workout, and building whole weeks just to
 * read their first day was most of the summary's cost on a long history.
 */
const mondayOf = (date) => mondayKey(date) ?? date;
/**
 * The rules of the program, as pure functions over the workout log.
 *
 * Nothing here is stored. "What goes on the bar next time" is recomputed from
 * the sessions every time it is asked, so two devices that have both synced
 * the same log always agree — and editing a past session (fixing a mis-tapped
 * set) corrects every figure after it, which a stored counter could not.
 *
 * StrongLifts' rules, which are the whole program:
 *   - Every set of a lift done → add weight next session.
 *   - Any set missed → same weight next session.
 *   - Missed three sessions running at the same weight → deload 10%.
 *   - Alternate workouts A and B.
 */
const LB_PER_KG = 2.2046226218;
export function convertWeight(weight, from, to) {
    if (from === to)
        return weight;
    return from === 'kg' ? weight * LB_PER_KG : weight / LB_PER_KG;
}
/**
 * Smallest step the bar can actually move: one of the smallest plates on
 * EACH side. With 1.25 kg plates that is 2.5 kg; with 2.5 lb plates, 5 lb.
 */
export function loadableStep(settings) {
    const smallest = Math.min(...(settings.plates.length ? settings.plates : [1.25]));
    return smallest * 2;
}
/** Hundredths, so plate arithmetic never accumulates 0.1 + 0.2 noise. */
const cents = (n) => Math.round(n * 100);
const fromCents = (n) => n / 100;
/**
 * Round to a weight that can be loaded: the bar plus whole steps. Never below
 * the empty bar — there is nothing lighter to lift.
 */
export function roundToLoadable(weight, settings, mode = 'nearest') {
    const bar = cents(settings.bar_weight);
    const step = cents(loadableStep(settings));
    const w = cents(weight);
    if (w <= bar)
        return settings.bar_weight;
    const steps = (w - bar) / step;
    const whole = mode === 'down' ? Math.floor(steps + 1e-9) : Math.round(steps);
    return fromCents(bar + whole * step);
}
/** Greedy plate calculator: what to put on each side of the bar. */
export function platesPerSide(total, barWeight, plates) {
    const sorted = [...plates].sort((a, b) => b - a).map(cents);
    let remaining = Math.max(0, Math.floor((cents(total) - cents(barWeight)) / 2));
    const perSide = [];
    for (const plate of sorted) {
        if (plate <= 0)
            continue;
        while (remaining >= plate) {
            perSide.push(fromCents(plate));
            remaining -= plate;
        }
    }
    const loaded = fromCents(cents(barWeight) + 2 * perSide.reduce((s, p) => s + cents(p), 0));
    return { perSide, exact: remaining === 0 && cents(total) >= cents(barWeight), loaded };
}
/** How the tap on a set circle cycles: empty → 5 → 4 → … → 0 → empty. */
export function cycleSetReps(current, target) {
    if (current === null)
        return target;
    if (current > 0)
        return current - 1;
    return null;
}
/** Rest after a set: short if it was hit, long if it was missed. */
export function restSecondsAfter(reps, target, settings) {
    if (reps === null)
        return 0;
    return reps >= target ? settings.rest_seconds.success : settings.rest_seconds.failed;
}
/** Every working set attempted and at target. */
export function exerciseSucceeded(ex) {
    return ex.sets.length > 0 && ex.sets.every((r) => r !== null && r >= ex.reps);
}
/** At least one set has been logged. An untouched exercise was skipped. */
export function exerciseAttempted(ex) {
    return ex.sets.some((r) => r !== null);
}
/**
 * Lists this function has already produced. Every figure here starts from
 * the sorted log, and the summary alone used to sort it once per lift —
 * most of its cost on a long history. Now the outer function sorts once and
 * passes the result down; handing a sorted list back in is free. Frozen, so
 * a caller cannot reorder one and leave it marked as sorted.
 */
const sortedLogs = new WeakSet();
/** Completed sessions, oldest first. In-progress ones never count. */
export function completedWorkouts(workouts) {
    if (sortedLogs.has(workouts))
        return workouts;
    const out = workouts
        .filter((w) => w.status === 'completed')
        .sort((a, b) => (a.date === b.date
        ? (a.finished_at ?? a.started_at).localeCompare(b.finished_at ?? b.started_at)
        : a.date.localeCompare(b.date)));
    Object.freeze(out);
    sortedLogs.add(out);
    return out;
}
/** One lift's attempted sessions, oldest first, converted to `unit`. */
export function liftHistory(workouts, lift, unit) {
    const out = [];
    for (const w of completedWorkouts(workouts)) {
        for (const ex of w.exercises ?? []) {
            if (ex.lift !== lift || !exerciseAttempted(ex))
                continue;
            out.push({
                workoutId: w.id,
                date: w.date,
                weight: convertWeight(ex.weight, w.unit ?? 'kg', unit),
                reps: ex.reps,
                sets: ex.sets,
                success: exerciseSucceeded(ex),
            });
        }
    }
    return out;
}
/** Failed sessions in a row needed to trigger a deload. */
export const DELOAD_AFTER_FAILURES = 3;
export const DELOAD_FACTOR = 0.9;
function incrementFor(lift, settings) {
    if (MAIN_LIFTS.includes(lift)) {
        return settings.increments[lift] ?? DEFAULT_INCREMENTS[settings.unit][lift];
    }
    const acc = [...settings.accessories.A, ...settings.accessories.B].find((a) => a.id === lift);
    return acc?.increment ?? 0;
}
function startWeightFor(lift, settings) {
    if (MAIN_LIFTS.includes(lift)) {
        return settings.start_weights[lift] ?? DEFAULT_START_WEIGHTS[settings.unit][lift];
    }
    const acc = [...settings.accessories.A, ...settings.accessories.B].find((a) => a.id === lift);
    return acc?.weight ?? 0;
}
/** What to load next time for one lift, and why. */
export function nextWeight(workouts, lift, settings) {
    const history = liftHistory(workouts, lift, settings.unit);
    const isMain = MAIN_LIFTS.includes(lift);
    const round = (w, mode = 'nearest') => (isMain ? roundToLoadable(w, settings, mode) : Math.max(0, Math.round(w * 100) / 100));
    if (history.length === 0) {
        return { weight: round(startWeightFor(lift, settings)), reason: 'start', failures: 0 };
    }
    const last = history[history.length - 1];
    if (last.success) {
        return { weight: round(last.weight + incrementFor(lift, settings)), reason: 'increase', failures: 0 };
    }
    let failures = 0;
    for (let i = history.length - 1; i >= 0; i--) {
        const s = history[i];
        if (s.success || Math.abs(s.weight - last.weight) > 0.01)
            break;
        failures++;
    }
    if (isMain && failures >= DELOAD_AFTER_FAILURES) {
        return { weight: round(last.weight * DELOAD_FACTOR, 'down'), reason: 'deload', failures };
    }
    return { weight: round(last.weight), reason: 'repeat', failures };
}
/** A then B then A… The first ever workout is A. */
export function nextWorkoutKey(workouts) {
    // A quick (squat-only) session leaves the rest of its workout owed, so it
    // does not move the alternation on.
    const done = completedWorkouts(workouts).filter((w) => !w.quick);
    const last = done[done.length - 1];
    return last?.workout === 'A' ? 'B' : 'A';
}
/** The exercises for the next session, weights already worked out. */
export function planWorkout(workouts, settings, key = nextWorkoutKey(workouts), opts = {}) {
    // Quick = the first lift only (squat in both A and B): the version of the
    // workout that fits a bad day, so a bad day doesn't become a skipped one.
    const program = FITNESS_PROGRAMS[settings.program][key];
    workouts = completedWorkouts(workouts);
    const main = (opts.quick ? program.slice(0, 1) : program).map((p) => ({
        lift: p.lift,
        name: liftName(p.lift),
        weight: nextWeight(workouts, p.lift, settings).weight,
        reps: p.reps,
        sets: Array.from({ length: p.sets }, () => null),
    }));
    const extras = (opts.quick ? [] : settings.accessories[key] ?? []).map((a) => ({
        lift: a.id,
        name: a.name,
        weight: nextWeight(workouts, a.id, settings).weight,
        reps: a.reps,
        sets: Array.from({ length: Math.max(1, a.sets) }, () => null),
        accessory: true,
    }));
    return [...main, ...extras];
}
/**
 * Warm-up ladder up to the working weight: the empty bar twice, then roughly
 * 40 / 60 / 80% with falling reps, so the lifter arrives warm, not tired.
 * Nothing at all when the work weight IS the empty bar.
 */
export function warmupSets(workWeight, lift, settings) {
    if (!MAIN_LIFTS.includes(lift))
        return [];
    const bar = settings.bar_weight;
    if (workWeight <= bar + 0.01)
        return [];
    const out = [];
    // Deadlifts start from the floor with plates on, so no empty-bar sets.
    if (lift !== 'deadlift')
        out.push({ weight: bar, reps: 5, sets: 2 });
    const ladder = lift === 'deadlift'
        ? [[0.4, 5], [0.6, 3], [0.85, 2]]
        : [[0.4, 5], [0.6, 3], [0.8, 2]];
    for (const [pct, reps] of ladder) {
        const w = roundToLoadable(workWeight * pct, settings, 'down');
        const prev = out[out.length - 1]?.weight ?? 0;
        if (w > prev + 0.01 && w < workWeight - 0.01)
            out.push({ weight: w, reps, sets: 1 });
    }
    return out;
}
/** Weight × reps over every logged working set, in `unit`. */
export function workoutVolume(w, unit = w.unit) {
    let total = 0;
    for (const ex of w.exercises ?? []) {
        const weight = convertWeight(ex.weight, w.unit ?? 'kg', unit);
        for (const r of ex.sets)
            if (r)
                total += weight * r;
    }
    return Math.round(total);
}
export function workoutMinutes(w) {
    if (!w.finished_at)
        return null;
    const ms = new Date(w.finished_at).getTime() - new Date(w.started_at).getTime();
    return ms > 0 ? Math.round(ms / 60000) : null;
}
/**
 * Weekly, not daily: rest days are part of the program, and a daily streak
 * would punish the lifter for doing exactly what it says. The current week
 * counts once it has hit the target, and does NOT break the streak while it
 * is still in progress — Monday morning should not read as a lost streak.
 */
export function weeklyStreak(workouts, target, today) {
    const perWeek = new Map();
    for (const w of completedWorkouts(workouts)) {
        const k = mondayOf(w.date);
        perWeek.set(k, (perWeek.get(k) ?? 0) + 1);
    }
    const goal = Math.max(1, target);
    const thisWeekKey = mondayOf(today);
    const thisWeek = perWeek.get(thisWeekKey) ?? 0;
    let current = thisWeek >= goal ? 1 : 0;
    for (let k = shiftDateKey(thisWeekKey, -7); (perWeek.get(k) ?? 0) >= goal; k = shiftDateKey(k, -7))
        current++;
    let best = 0;
    const weeks = [...perWeek.keys()].sort();
    if (weeks.length) {
        let run = 0;
        for (let k = weeks[0]; k <= thisWeekKey; k = shiftDateKey(k, 7)) {
            if ((perWeek.get(k) ?? 0) >= goal) {
                run++;
                best = Math.max(best, run);
            }
            else
                run = 0;
        }
    }
    return { current, best: Math.max(best, current), thisWeek, target: goal };
}
/** Epley: weight × (1 + reps/30). One rep is the weight itself. */
export function estimatedOneRepMax(weight, reps) {
    if (reps <= 0 || weight <= 0)
        return 0;
    return reps === 1 ? weight : weight * (1 + reps / 30);
}
/** Personal records per main lift, plus any accessory with weight. */
export function personalRecords(workouts, unit) {
    const byLift = new Map();
    for (const w of completedWorkouts(workouts)) {
        for (const ex of w.exercises ?? []) {
            if (!exerciseAttempted(ex) || ex.weight <= 0)
                continue;
            const weight = convertWeight(ex.weight, w.unit ?? 'kg', unit);
            const rec = byLift.get(ex.lift) ?? {
                lift: ex.lift, name: ex.name || liftName(ex.lift), bestWeight: 0, bestWeightDate: '', estimatedMax: 0,
            };
            if (exerciseSucceeded(ex) && weight > rec.bestWeight + 0.01) {
                rec.bestWeight = weight;
                rec.bestWeightDate = w.date;
            }
            for (const r of ex.sets) {
                if (r)
                    rec.estimatedMax = Math.max(rec.estimatedMax, estimatedOneRepMax(weight, r));
            }
            byLift.set(ex.lift, rec);
        }
    }
    const order = (l) => {
        const i = MAIN_LIFTS.indexOf(l);
        return i === -1 ? 99 : i;
    };
    return [...byLift.values()]
        .map((r) => ({ ...r, bestWeight: round1(r.bestWeight), estimatedMax: round1(r.estimatedMax) }))
        .sort((a, b) => order(a.lift) - order(b.lift) || a.name.localeCompare(b.name));
}
const round1 = (n) => Math.round(n * 10) / 10;
/**
 * Lifts where this workout set a new heaviest completed weight. The first
 * time a lift is completed at all counts — every number is a record once.
 */
export function newRecordsIn(workout, all, unit) {
    const before = all.filter((w) => w.id !== workout.id && w.status === 'completed'
        && (w.date < workout.date || (w.date === workout.date
            && (w.finished_at ?? w.started_at) < (workout.finished_at ?? workout.started_at))));
    const prior = new Map(personalRecords(before, unit).map((r) => [r.lift, r.bestWeight]));
    const out = [];
    for (const ex of workout.exercises ?? []) {
        if (!exerciseSucceeded(ex) || ex.weight <= 0)
            continue;
        const weight = convertWeight(ex.weight, workout.unit ?? 'kg', unit);
        if (weight > (prior.get(ex.lift) ?? 0) + 0.01)
            out.push(ex.lift);
    }
    return out;
}
/** Latest weigh-in on or before `date`, in `unit`. */
export function latestBodyweight(entries, unit, date) {
    const sorted = entries
        .filter((e) => e.weight > 0 && (!date || e.date <= date))
        .sort((a, b) => a.date.localeCompare(b.date));
    const last = sorted[sorted.length - 1];
    return last ? { ...last, inUnit: round1(convertWeight(last.weight, last.unit ?? 'kg', unit)) } : null;
}
/**
 * Where bodyweight is heading. Compares the latest weigh-in with the latest
 * one at least `days` earlier — not "exactly a week ago", because nobody
 * weighs in every single day.
 */
export function bodyweightTrend(entries, unit, today, days = 7) {
    const latest = latestBodyweight(entries, unit, today);
    if (!latest)
        return null;
    const before = latestBodyweight(entries, unit, shiftDateKey(latest.date, -days));
    return {
        latest: latest.inUnit,
        latestDate: latest.date,
        loggedToday: latest.date === today,
        change: before ? round1(latest.inUnit - before.inUnit) : null,
        sinceDate: before?.date ?? null,
    };
}
const LIFT_MILESTONES = {
    kg: [['squat', 100], ['bench', 60], ['deadlift', 140], ['ohp', 50], ['row', 70]],
    lb: [['squat', 225], ['bench', 135], ['deadlift', 315], ['ohp', 95], ['row', 155]],
};
const VOLUME_MILESTONES = {
    kg: [10_000, 100_000, 1_000_000],
    lb: [25_000, 250_000, 2_500_000],
};
export function fitnessBadges(workouts, bodyweights, settings, today) {
    const done = completedWorkouts(workouts);
    const count = done.length;
    const unit = settings.unit;
    const streak = weeklyStreak(done, settings.weekly_target, today);
    const records = new Map(personalRecords(done, unit).map((r) => [r.lift, r.bestWeight]));
    const volume = done.reduce((s, w) => s + workoutVolume(w, unit), 0);
    const perfect = done.some((w) => w.exercises.length > 0 && w.exercises.every(exerciseSucceeded));
    const bw = latestBodyweight(bodyweights, unit)?.inUnit ?? 0;
    const clamp = (n) => Math.max(0, Math.min(1, n));
    const badges = [];
    badges.push({ id: 'first-workout', name: 'Day One', description: 'Finish your first workout', icon: '🏁', earned: count >= 1, progress: clamp(count) });
    for (const n of [10, 25, 50, 100, 250]) {
        badges.push({ id: `workouts-${n}`, name: `${n} Workouts`, description: `Finish ${n} workouts`, icon: n >= 100 ? '🏆' : '💪', earned: count >= n, progress: clamp(count / n) });
    }
    badges.push({ id: 'perfect-workout', name: 'Clean Sweep', description: 'Hit every rep of every set in one workout', icon: '✨', earned: perfect, progress: perfect ? 1 : 0 });
    for (const n of [4, 12, 26, 52]) {
        badges.push({ id: `streak-${n}`, name: `${n}-Week Streak`, description: `Hit your weekly target ${n} weeks running`, icon: '🔥', earned: streak.best >= n, progress: clamp(streak.best / n) });
    }
    for (const [lift, threshold] of LIFT_MILESTONES[unit]) {
        const best = records.get(lift) ?? 0;
        badges.push({ id: `lift-${lift}-${threshold}${unit}`, name: `${liftName(lift)} ${threshold} ${unit}`, description: `Complete every set of ${liftName(lift).toLowerCase()} at ${threshold} ${unit}`, icon: '🏋️', earned: best >= threshold, progress: clamp(best / threshold) });
    }
    if (bw > 0) {
        const squat = records.get('squat') ?? 0;
        badges.push({ id: 'bodyweight-squat', name: 'Squat Your Bodyweight', description: 'Complete a squat workout at your bodyweight', icon: '⚖️', earned: squat >= bw, progress: clamp(squat / bw) });
    }
    for (const v of VOLUME_MILESTONES[unit]) {
        badges.push({ id: `volume-${v}${unit}`, name: `${v.toLocaleString('en-US')} ${unit} Lifted`, description: `Lift ${v.toLocaleString('en-US')} ${unit} in total`, icon: '📈', earned: volume >= v, progress: clamp(volume / v) });
    }
    return badges.map((b) => ({ ...b, percent: Math.round(b.progress * 100) }));
}
export function fitnessSummary(workouts, settings, today) {
    const done = completedWorkouts(workouts);
    const last = done[done.length - 1];
    const key = nextWorkoutKey(done);
    const exercises = planWorkout(done, settings, key).map((ex) => ({
        lift: ex.lift,
        name: ex.name,
        weight: ex.weight,
        sets: ex.sets.length,
        reps: ex.reps,
        reason: ex.accessory ? undefined : nextWeight(done, ex.lift, settings).reason,
    }));
    const inProgress = inProgressWorkout(workouts);
    return {
        totalWorkouts: done.length,
        streak: weeklyStreak(done, settings.weekly_target, today),
        totalVolume: done.reduce((s, w) => s + workoutVolume(w, settings.unit), 0),
        unit: settings.unit,
        lastWorkoutDate: last?.date ?? null,
        daysSinceLast: last ? daysBetween(parseDateKey(today) ?? new Date(), parseDateKey(last.date) ?? new Date()) : null,
        next: { workout: key, exercises },
        records: personalRecords(done, settings.unit),
        inProgress,
        trainingDay: settings.workout_days.includes(parseDateKey(today)?.getDay() ?? -1),
    };
}
/** Dates in [from, to] with at least one completed workout. For the heatmap. */
export function workoutDates(workouts) {
    return new Set(completedWorkouts(workouts).map((w) => w.date));
}
// ─── Changes ───────────────────────────────────────────────────────────────
//
// Every change to the log, defined once. The Fitness view and the MCP
// server's write_fitness both call these and then persist the result — the
// app through its storage adapter, the server into the snapshot it pushes —
// so a set logged by voice is byte-for-byte the set the screen would log.
// Same pattern as completeTaskEffect. All return NEW objects; nothing here
// mutates its input.
const touch = (row, now) => ({ ...row, updated_at: now.toISOString() });
/** The in-progress session, if there is one (newest wins). */
export function inProgressWorkout(workouts) {
    return workouts
        .filter((w) => w.status === 'in_progress')
        .sort((a, b) => b.started_at.localeCompare(a.started_at))[0] ?? null;
}
/**
 * Begin a session. An unfinished one is RESUMED rather than duplicated — a
 * second tap on Start, or "start my workout" said twice, must not leave two
 * half-logged sessions competing to be the record.
 */
export function startWorkoutEffect(workouts, settings, today, key, now = new Date(), opts = {}) {
    const open = inProgressWorkout(workouts);
    if (open)
        return { workout: open, resumed: true };
    const workout = nextWorkoutKeyOr(workouts, key);
    return {
        workout: createFitnessWorkout({
            date: today,
            workout,
            program: settings.program,
            unit: settings.unit,
            exercises: planWorkout(workouts, settings, workout, opts),
            quick: opts.quick,
        }, now),
        resumed: false,
    };
}
const nextWorkoutKeyOr = (workouts, key) => key ?? nextWorkoutKey(workouts);
function withExercise(workout, index, change, now) {
    if (index < 0 || index >= workout.exercises.length)
        return workout;
    const exercises = workout.exercises.map((ex, i) => (i === index ? change(ex) : ex));
    return touch({ ...workout, exercises }, now);
}
/** Set one working set's reps (null clears it). */
export function logSetEffect(workout, exerciseIndex, setIndex, reps, now = new Date()) {
    return withExercise(workout, exerciseIndex, (ex) => {
        if (setIndex < 0 || setIndex >= ex.sets.length)
            return ex;
        const sets = [...ex.sets];
        sets[setIndex] = reps === null ? null : Math.max(0, Math.round(reps));
        return { ...ex, sets };
    }, now);
}
/** The tap on a set circle: empty → target → one fewer … → 0 → empty. */
export function tapSetEffect(workout, exerciseIndex, setIndex, now = new Date()) {
    const ex = workout.exercises[exerciseIndex];
    if (!ex)
        return workout;
    return logSetEffect(workout, exerciseIndex, setIndex, cycleSetReps(ex.sets[setIndex] ?? null, ex.reps), now);
}
/** Change the weight on the bar for one exercise in this session. */
export function setExerciseWeightEffect(workout, exerciseIndex, weight, now = new Date()) {
    return withExercise(workout, exerciseIndex, (ex) => ({ ...ex, weight: Math.max(0, weight) }), now);
}
/** Close the session. Its sets now count towards progression and records. */
export function finishWorkoutEffect(workout, now = new Date(), notes) {
    return touch({
        ...workout,
        status: 'completed',
        finished_at: workout.finished_at ?? now.toISOString(),
        ...(notes !== undefined ? { notes } : {}),
    }, now);
}
/**
 * Record a whole session after the fact — "I did workout A, all fives, but
 * only 4 on the last two bench sets". Starts from exactly what the screen
 * would have offered (planWorkout), then applies what was said. An exercise
 * not mentioned keeps its planned weight and, if `allDone`, its target reps;
 * otherwise it is left unattempted, which progression treats as skipped.
 */
export function recordWorkoutEffect(workouts, settings, input, now = new Date()) {
    const key = nextWorkoutKeyOr(workouts, input.workout);
    let planned = planWorkout(workouts, settings, key, { quick: input.quick }).map((ex) => (input.allDone
        ? { ...ex, sets: ex.sets.map(() => ex.reps) }
        : ex));
    const unknownLifts = [];
    for (const said of input.exercises ?? []) {
        const needle = said.lift.trim().toLowerCase();
        const i = planned.findIndex((ex) => ex.lift.toLowerCase() === needle || ex.name.toLowerCase() === needle);
        if (i === -1) {
            unknownLifts.push(said.lift);
            continue;
        }
        planned = planned.map((ex, j) => {
            if (j !== i)
                return ex;
            const sets = said.sets
                ? ex.sets.map((_, k) => (said.sets[k] === undefined ? null : Math.max(0, Math.round(said.sets[k]))))
                : ex.sets.map(() => ex.reps);
            return { ...ex, weight: said.weight ?? ex.weight, sets };
        });
    }
    const draft = createFitnessWorkout({ date: input.date, workout: key, program: settings.program, unit: settings.unit, exercises: planned, quick: input.quick }, now);
    return { workout: finishWorkoutEffect(draft, now, input.notes ?? ''), unknownLifts };
}
/** One weigh-in per day: a second entry for the same date replaces the first. */
export function bodyweightEffect(existing, date, weight, unit, now = new Date()) {
    const prior = existing.find((e) => e.date === date);
    const fresh = createFitnessBodyweight(date, weight, unit, now);
    return prior ? { ...fresh, created_at: prior.created_at } : fresh;
}
/** Apply a settings change, stamped so it wins the next sync merge. */
export function updateFitnessSettingsEffect(settings, patch, now = new Date()) {
    return touch(normalizeFitnessSettings({ ...settings, ...patch }), now);
}
/**
 * Switch kg ↔ lb. The bar and plates become that unit's standard set, and
 * every weight the user typed (start weights, accessory weights) is converted
 * and rounded to something loadable. Increment overrides are dropped, because
 * "2.5" means a different jump in each unit; the unit's defaults apply.
 * The log itself is untouched — every workout records its own unit, and
 * progression converts on read.
 */
export function changeFitnessUnitEffect(settings, unit, now = new Date()) {
    if (settings.unit === unit)
        return settings;
    const base = { plates: DEFAULT_PLATES[unit], bar_weight: DEFAULT_BAR_WEIGHT[unit] };
    const start_weights = {};
    for (const [lift, w] of Object.entries(settings.start_weights)) {
        if (typeof w === 'number')
            start_weights[lift] = roundToLoadable(convertWeight(w, settings.unit, unit), base);
    }
    const convertAcc = (list) => list.map((a) => ({
        ...a,
        weight: a.weight ? Math.round(convertWeight(a.weight, settings.unit, unit) * 2) / 2 : a.weight,
        increment: a.increment ? DEFAULT_INCREMENTS[unit].squat : a.increment,
    }));
    return updateFitnessSettingsEffect(settings, {
        unit,
        ...base,
        plates: [...base.plates],
        start_weights,
        increments: {},
        accessories: { A: convertAcc(settings.accessories.A), B: convertAcc(settings.accessories.B) },
    }, now);
}
// ─── The workout habit ─────────────────────────────────────────────────────
//
// Going to the gym is a habit, and this app already knows how to make habits
// happen: it schedules them into the day around meetings, streaks them,
// reviews them, links them to goals and pays XP for them. So the gym is not
// a parallel system — it is ONE ordinary habit task with `habit_kind:
// 'workout'`. Every system that counts habits sees a habit. Three things are
// special, and all three live here:
//
//   1. How it is ticked: by finishing a workout (`workoutHabitDate` picks
//      which due day that counts for), not by its checkbox.
//   2. Its days ARE the training days. The program reads them from the
//      habit (`withTrainingPlan`), so there is one day picker, not two.
//   3. Its length follows how long workouts actually take
//      (`typicalWorkoutMinutes`), so the scheduler books a realistic slot.
export function isWorkoutHabit(task) {
    return Boolean(task?.is_habit && task.habit_kind === 'workout');
}
/** The user's workout habit: the oldest one not archived. One per person. */
export function findWorkoutHabit(tasks) {
    return tasks
        .filter((t) => isWorkoutHabit(t) && !t.is_archived)
        .sort((a, b) => a.created_at.localeCompare(b.created_at))[0] ?? null;
}
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
/**
 * "3 days a week" as actual days, spread so there is rest between them —
 * the shape the habit day picker starts from, and the user can move.
 */
export const TRAINING_DAY_PRESETS = {
    1: ['Wednesday'],
    2: ['Tuesday', 'Friday'],
    3: ['Monday', 'Wednesday', 'Friday'],
    4: ['Monday', 'Tuesday', 'Thursday', 'Friday'],
    5: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    6: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    7: [...DAY_NAMES.slice(1), 'Sunday'],
};
export function dayNumbersToNames(days) {
    return [...days].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7)).map((d) => DAY_NAMES[d]).filter(Boolean);
}
/**
 * Which weekdays (0 = Sunday) the habit's recurrence lands on — the habit's
 * own predicate, run over a week. Pause, archive and start date are ignored
 * on purpose: this is the habit's PATTERN, which is what the program plans
 * to, not whether this particular week is on.
 */
export function trainingDayNumbers(habit, anchor) {
    const subject = { ...scheduleSubject(habit), is_paused: false, paused_until: null, is_archived: false, created_at: undefined };
    return weekKeys(anchor)
        .filter((d) => habitScheduledOn(subject, d))
        .map((d) => parseDateKey(d)?.getDay() ?? -1)
        .filter((d) => d >= 0);
}
/**
 * Settings with the training days and weekly target taken from the workout
 * habit, when there is one. Pass THIS to everything that plans or streaks,
 * so "3 days a week" means the same thing on Today, in Fitness and in the
 * habit's own streak. Without a habit, the settings are used as they are.
 */
export function withTrainingPlan(settings, habit, today) {
    if (!habit)
        return settings;
    const days = trainingDayNumbers(habit, today);
    if (days.length === 0)
        return settings;
    return { ...settings, workout_days: days, weekly_target: days.length };
}
/**
 * How long a workout really takes: the median of the last six full sessions,
 * rounded to five minutes. Feeds the habit's length so the scheduler books a
 * slot that fits. 60 until there is history.
 */
export function typicalWorkoutMinutes(workouts, fallback = 60) {
    const recent = completedWorkouts(workouts)
        .filter((w) => !w.quick)
        .map(workoutMinutes)
        .filter((m) => m !== null && m >= 10 && m <= 240)
        .slice(-6)
        .sort((a, b) => a - b);
    if (recent.length === 0)
        return fallback;
    const mid = recent.length / 2;
    const median = recent.length % 2 ? recent[Math.floor(mid)] : (recent[mid - 1] + recent[mid]) / 2;
    return Math.min(180, Math.max(15, Math.round(median / 5) * 5));
}
/** A new workout habit, on the days the program was already set to. */
export function createWorkoutHabitEffect(settings, workouts, opts = {}) {
    return createTask({
        project_id: '',
        title: 'Workout',
        description: 'Ticked by finishing a workout in Fitness.',
        is_habit: true,
        habit_kind: 'workout',
        energy_level: 'high',
        priority: 4,
        difficulty: 4,
        estimated_minutes: typicalWorkoutMinutes(workouts),
        tags: ['fitness'],
        recurrence: {
            frequency: 'weekly',
            days_of_week: opts.days?.length ? opts.days : dayNumbersToNames(settings.workout_days),
            time_of_day: opts.time_of_day ?? null,
        },
    });
}
/** Change the training days — which is changing the habit's days. */
export function setTrainingDaysEffect(habit, days, now = new Date()) {
    return applyTaskUpdate(habit, { recurrence: { frequency: 'weekly', days_of_week: days } }, now.toISOString());
}
/** Keep the habit's length in step with real workouts. Null = no change. */
export function workoutHabitDurationUpdate(habit, workouts, now = new Date()) {
    const minutes = typicalWorkoutMinutes(workouts, habit.estimated_minutes || 60);
    return minutes === habit.estimated_minutes ? null : applyTaskUpdate(habit, { estimated_minutes: minutes }, now.toISOString());
}
/**
 * Which due day a workout on `workoutDate` ticks.
 *
 * - The day itself, if it was a training day still open.
 * - Otherwise the EARLIEST missed training day earlier in the same week —
 *   that is a catch-up, and it should repair the streak rather than leave a
 *   hole on Monday and an unexplained extra on Tuesday.
 * - Otherwise null: an extra session. It still counts in Fitness (weekly
 *   total, records, progression); there is just no habit day for it to fill.
 */
export function workoutHabitDate(habit, instances, workoutDate) {
    const subject = scheduleSubject(habit);
    const done = new Set(instances.filter((i) => i.habit_id === habit.id).map((i) => i.date));
    if (habitScheduledOn(subject, workoutDate) && !done.has(workoutDate))
        return workoutDate;
    return missedTrainingDays(habit, instances, workoutDate)[0] ?? null;
}
/**
 * The habit day a workout ticked, so deleting the workout can undo exactly
 * that tick (through the ordinary uncompleteTaskEffect — XP reversed by the
 * amount recorded, streak recomputed). Null for an extra session, a workout
 * still in progress, or a tick made by hand.
 */
export function workoutHabitTick(habit, instances, workoutId) {
    return instances.find((i) => i.habit_id === habit.id && i.workout_id === workoutId)?.date ?? null;
}
/** Training days earlier this week (Monday-first) that were not done. */
export function missedTrainingDays(habit, instances, today) {
    const monday = weekKeys(today)[0];
    if (!monday || monday >= today)
        return [];
    const done = new Set(instances.filter((i) => i.habit_id === habit.id).map((i) => i.date));
    return habitDueDates(scheduleSubject(habit), monday, shiftDateKey(today, -1)).filter((d) => !done.has(d));
}
/**
 * What Today should say about the gym, in one word.
 *
 *   in_progress  a workout is open — resume it
 *   done         today's (or a catch-up) workout is finished
 *   training     today is a training day and it hasn't happened yet
 *   catch_up     not a training day, but one earlier this week was missed
 *   rest         nothing owed today
 *   none         there is no workout habit
 */
export function workoutDayStatus(habit, instances, workouts, today) {
    if (!habit)
        return { kind: 'none', missed: [], remainingThisWeek: [] };
    const subject = scheduleSubject(habit);
    const sunday = weekKeys(today)[6] ?? today;
    const remainingThisWeek = today < sunday ? habitDueDates(subject, shiftDateKey(today, 1), sunday) : [];
    const missed = missedTrainingDays(habit, instances, today);
    const base = { missed, remainingThisWeek };
    if (inProgressWorkout(workouts))
        return { kind: 'in_progress', ...base };
    const doneToday = completedWorkouts(workouts).some((w) => w.date === today)
        || instances.some((i) => i.habit_id === habit.id && i.date === today);
    if (doneToday)
        return { kind: 'done', ...base };
    if (habitPausedOn(subject, today) || habit.is_archived)
        return { kind: 'rest', ...base };
    if (habitScheduledOn(subject, today))
        return { kind: 'training', ...base };
    if (missed.length > 0)
        return { kind: 'catch_up', ...base };
    return { kind: 'rest', ...base };
}
// ─── Goals and reviews ─────────────────────────────────────────────────────
/**
 * "Squat 100 kg" as a goal: one key result that follows the lift. Not linked
 * to the workout habit on purpose — goal progress averages its contributors,
 * so adding the habit would blur "how close is the squat" into "how often
 * did I go". Link it by hand if that is what you want measured.
 */
export function createLiftGoalEffect(lift, target, unit, workouts, opts = {}) {
    const name = liftName(lift);
    const best = personalRecords(workouts, unit).find((r) => r.lift === lift)?.bestWeight ?? 0;
    const stamp = (opts.now ?? new Date()).toISOString();
    const goal = createGoal({
        title: `${name} ${target} ${unit}`,
        description: `Every set of ${name.toLowerCase()} completed at ${target} ${unit}. Progress follows your best in Fitness.`,
        category: 'health',
        time_horizon: 'quarterly',
        target_date: opts.target_date ?? null,
    });
    const kr = { id: uuidv4(), label: name, target, current: best, unit, lift, created_at: stamp, updated_at: stamp };
    return { ...goal, key_results: [kr] };
}
/**
 * Bring every lift key result up to the current best. Returns ONLY the goals
 * that changed, ready to save. A KR in the other unit is converted, so a
 * goal set in lb keeps working after a switch to kg.
 */
export function syncLiftKeyResults(goals, workouts, now = new Date()) {
    const stamp = now.toISOString();
    const best = { kg: new Map(personalRecords(workouts, 'kg').map((r) => [r.lift, r.bestWeight])),
        lb: new Map(personalRecords(workouts, 'lb').map((r) => [r.lift, r.bestWeight])) };
    const changed = [];
    for (const goal of goals) {
        let touched = false;
        const key_results = (goal.key_results ?? []).map((kr) => {
            if (!kr.lift)
                return kr;
            const unit = kr.unit === 'lb' ? 'lb' : 'kg';
            const current = best[unit].get(kr.lift) ?? 0;
            if (Math.abs(current - kr.current) < 0.05)
                return kr;
            touched = true;
            return { ...kr, current, updated_at: stamp };
        });
        if (touched)
            changed.push({ ...goal, key_results, updated_at: stamp });
    }
    return changed;
}
/** The gym, over a review period. Null when nothing happened in it. */
export function fitnessPeriodSummary(workouts, start, end, unit) {
    const done = completedWorkouts(workouts);
    const inPeriod = done.filter((w) => w.date >= start && w.date <= end);
    if (inPeriod.length === 0)
        return null;
    // One pass, oldest first, carrying the best so far per lift: a workout in
    // the period set a record if it beat everything before it — the same rule
    // as newRecordsIn, without recomputing the whole history per workout.
    const best = new Map();
    const records = new Map();
    for (const w of done) {
        if (w.date > end)
            break;
        for (const ex of w.exercises ?? []) {
            if (!exerciseSucceeded(ex) || ex.weight <= 0)
                continue;
            const weight = convertWeight(ex.weight, w.unit ?? 'kg', unit);
            if (weight > (best.get(ex.lift) ?? 0) + 0.01) {
                best.set(ex.lift, weight);
                if (w.date >= start)
                    records.set(ex.lift, { lift: ex.lift, name: ex.name || liftName(ex.lift), weight: round1(weight) });
            }
        }
    }
    return {
        workouts: inPeriod.length,
        quick: inPeriod.filter((w) => w.quick).length,
        volume: inPeriod.reduce((s, w) => s + workoutVolume(w, unit), 0),
        unit,
        records: [...records.values()],
    };
}
//# sourceMappingURL=fitness.js.map